<template>
  <div class="ac-page">
    <div class="ac-header">
      <h1 class="ac-title">折扣券管理</h1>
      <button class="ac-btn ac-btn--primary" @click="openAdd">＋ 新增折扣券</button>
    </div>

    <p class="ac-hint">全站折扣券：消費滿指定金額，享指定折數優惠，自動於購物車顯示。</p>

    <!-- 載入中 -->
    <div v-if="loading" class="ac-state">載入中…</div>

    <!-- 空 -->
    <div v-else-if="coupons.length === 0" class="ac-state">
      尚無折扣券，點右上角「＋ 新增折扣券」建立第一張。
    </div>

    <!-- 表格 -->
    <table v-else class="ac-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>最低消費（NT$）</th>
          <th>折數</th>
          <th>循環倒數</th>
          <th>說明</th>
          <th>狀態</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="c in coupons" :key="c.id">
          <td>{{ c.id }}</td>
          <td>{{ c.min_amount.toLocaleString() }}</td>
          <td>{{ Number(c.discount_rate) }}折</td>
          <td>
            <span v-if="c.cycle_days" class="ac-cycle">
              循環 {{ c.cycle_days }} 天
              <span v-if="c.days_left != null" class="ac-days-left">倒數 {{ c.days_left }} 天</span>
            </span>
            <span v-else class="ac-no-cycle">永不過期</span>
          </td>
          <td class="ac-desc">消費滿 NT${{ c.min_amount.toLocaleString() }} 享 {{ Number(c.discount_rate) }} 折</td>
          <td>
            <span class="ac-badge" :class="c.is_active ? 'ac-badge--on' : 'ac-badge--off'">
              {{ c.is_active ? '啟用' : '停用' }}
            </span>
          </td>
          <td class="ac-actions">
            <button class="ac-btn ac-btn--sm ac-btn--secondary" @click="openEdit(c)">編輯</button>
            <button class="ac-btn ac-btn--sm ac-btn--toggle" @click="toggleActive(c)">
              {{ c.is_active ? '停用' : '啟用' }}
            </button>
            <button class="ac-btn ac-btn--sm ac-btn--danger" @click="deleteCoupon(c)">刪除</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 新增 / 編輯 Modal -->
    <teleport to="body">
      <div v-if="showModal" class="ac-overlay" @click.self="closeModal">
        <div class="ac-modal">
          <div class="ac-modal__header">
            <h2>{{ editingId ? '編輯折扣券' : '新增折扣券' }}</h2>
            <button class="ac-modal__close" @click="closeModal">✕</button>
          </div>
          <div class="ac-modal__body">
            <div class="ac-field">
              <label>最低消費金額（NT$） <span class="ac-req">*</span></label>
              <input v-model.number="form.min_amount" type="number" min="1" class="ac-input"
                placeholder="例：500" />
              <p v-if="errors.min_amount" class="ac-err">{{ errors.min_amount }}</p>
            </div>
            <div class="ac-field">
              <label>折數 <span class="ac-req">*</span>
                <span class="ac-hint-inline">（例：9 = 9折，9.5 = 9.5折，8 = 8折，範圍 0.1 ~ 9.9）</span>
              </label>
              <div class="ac-input-row">
                <input v-model.number="form.discount_rate" type="number" min="0.1" max="9.9" step="0.1" class="ac-input"
                  placeholder="例：9.5" style="flex:1" />
                <span class="ac-input-suffix">折</span>
              </div>
              <p v-if="errors.discount_rate" class="ac-err">{{ errors.discount_rate }}</p>
            </div>
            <div class="ac-field">
              <label>循環天數（選填）
                <span class="ac-hint-inline">空白 = 永不過期；填入天數後每次到期自動重置</span>
              </label>
              <div class="ac-input-row">
                <input v-model.number="form.cycle_days" type="number" min="1" class="ac-input"
                  placeholder="例：7（留空表示永不過期）" style="flex:1" />
                <span class="ac-input-suffix">天</span>
              </div>
            </div>
            <div class="ac-field">
              <label>
                <input type="checkbox" v-model="form.is_active" style="margin-right:6px" />
                立即啟用
              </label>
            </div>
            <!-- 預覽 -->
            <div v-if="previewValid" class="ac-preview">
              預覽：消費滿 NT${{ form.min_amount.toLocaleString() }} 享 {{ form.discount_rate }} 折
              <span class="ac-preview-sub">
                （例：消費 NT${{ form.min_amount.toLocaleString() }} → 券後 NT${{ previewAfter.toLocaleString() }}，省 NT${{ previewSave.toLocaleString() }}）
              </span>
            </div>
            <p v-if="apiError" class="ac-err">{{ apiError }}</p>
          </div>
          <div class="ac-modal__footer">
            <button class="ac-btn ac-btn--secondary" @click="closeModal">取消</button>
            <button class="ac-btn ac-btn--primary" :disabled="submitting" @click="save">
              {{ submitting ? '儲存中…' : '儲存' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'AdminCoupons',
  data() {
    return {
      coupons: [],
      loading: false,
      showModal: false,
      editingId: null,
      form: { min_amount: '', discount_rate: '', cycle_days: '', is_active: true },
      errors: {},
      apiError: '',
      submitting: false,
    };
  },
  computed: {
    previewValid() {
      const r = Number(this.form.discount_rate);
      return this.form.min_amount > 0 && r > 0 && r < 10;
    },
    previewAfter() {
      return Math.round(Number(this.form.min_amount) * Number(this.form.discount_rate) / 10);
    },
    previewSave() {
      return Number(this.form.min_amount) - this.previewAfter;
    },
  },
  async created() {
    await this.fetchCoupons();
  },
  methods: {
    async fetchCoupons() {
      this.loading = true;
      try {
        const res = await axios.get('/api/admin/coupons');
        if (res.data.success) this.coupons = res.data.coupons;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.editingId = null;
      this.form = { min_amount: '', discount_rate: '', cycle_days: '', is_active: true };
      this.errors = {};
      this.apiError = '';
      this.showModal = true;
    },
    openEdit(c) {
      this.editingId = c.id;
      this.form = { min_amount: c.min_amount, discount_rate: Number(c.discount_rate), cycle_days: c.cycle_days || '', is_active: !!c.is_active };
      this.errors = {};
      this.apiError = '';
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    validate() {
      const e = {};
      if (!this.form.min_amount || this.form.min_amount <= 0) {
        e.min_amount = '請輸入有效的最低消費金額';
      }
      const r = Number(this.form.discount_rate);
      if (isNaN(r) || r <= 0 || r >= 10) {
        e.discount_rate = '折數必須介於 0.1 ~ 9.9 之間（例：9.5 代表 9.5折）';
      }
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    async save() {
      if (!this.validate()) return;
      this.submitting = true;
      this.apiError = '';
      try {
        const payload = { ...this.form };
        const res = this.editingId
          ? await axios.put(`/api/admin/coupons/${this.editingId}`, payload)
          : await axios.post('/api/admin/coupons', payload);
        if (res.data.success) {
          this.closeModal();
          await this.fetchCoupons();
        } else {
          this.apiError = res.data.message || '儲存失敗';
        }
      } catch (e) {
        this.apiError = e.response?.data?.message || '伺服器錯誤';
      } finally {
        this.submitting = false;
      }
    },
    async toggleActive(c) {
      try {
        await axios.put(`/api/admin/coupons/${c.id}`, {
          min_amount: c.min_amount,
          discount_rate: c.discount_rate,
          cycle_days: c.cycle_days || null,
          is_active: c.is_active ? 0 : 1,
        });
        await this.fetchCoupons();
      } catch (e) {
        alert('操作失敗');
      }
    },
    async deleteCoupon(c) {
      const result = await Swal.fire({
        title: `確定刪除「滿 NT$${c.min_amount} 享 ${Number(c.discount_rate)} 折」嗎？`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定刪除',
        cancelButtonText: '取消',
        confirmButtonColor: '#e74c3c',
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`/api/admin/coupons/${c.id}`);
        await this.fetchCoupons();
      } catch (e) {
        alert('刪除失敗');
      }
    },
  },
};
</script>

<style scoped>
.ac-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 20px;
}
.ac-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 12px;
}
.ac-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}
.ac-hint {
  font-size: 0.88rem;
  color: #7f8c8d;
  margin: 0 0 24px;
}
.ac-hint-inline {
  font-size: 0.78rem;
  color: #999;
  font-weight: 400;
}
.ac-state {
  text-align: center;
  color: #7f8c8d;
  padding: 40px 0;
  font-size: 0.95rem;
}

/* 表格 */
.ac-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
}
.ac-table th,
.ac-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #eaeaea;
  text-align: left;
  vertical-align: middle;
}
.ac-table thead th {
  background: #f4f6f8;
  font-weight: 600;
  color: #555;
}
.ac-desc { color: #5d6d7e; }
.ac-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
}
.ac-badge--on  { background: #d4edda; color: #155724; }
.ac-badge--off { background: #e9ecef; color: #6c757d; }

.ac-actions { display: flex; gap: 6px; flex-wrap: wrap; }

/* 按鈕 */
.ac-btn {
  padding: 8px 18px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 600;
  transition: opacity .2s;
}
.ac-btn:disabled { opacity: .6; cursor: not-allowed; }
.ac-btn--primary   { background: #2E3A59; color: #fff; }
.ac-btn--primary:hover { opacity: .85; }
.ac-btn--secondary { background: #e9ecef; color: #495057; }
.ac-btn--secondary:hover { background: #dee2e6; }
.ac-btn--toggle { background: #fff3cd; color: #856404; }
.ac-btn--toggle:hover { background: #ffeeba; }
.ac-btn--danger  { background: #f8d7da; color: #721c24; }
.ac-btn--danger:hover { background: #f5c6cb; }
.ac-btn--sm { padding: 5px 12px; font-size: 0.82rem; }

/* 輸入列 */
.ac-input-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ac-input-suffix {
  font-size: 0.95rem;
  font-weight: 600;
  color: #444;
  white-space: nowrap;
}

/* Modal */
.ac-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.5);
  z-index: 2000;
  display: flex; align-items: center; justify-content: center;
}
.ac-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 8px 40px rgba(0,0,0,.18);
  overflow: hidden;
}
.ac-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px;
  background: #f4f6f8;
  border-bottom: 1px solid #eaeaea;
}
.ac-modal__header h2 { margin: 0; font-size: 1.1rem; color: #2c3e50; }
.ac-modal__close {
  background: none; border: none; font-size: 1.1rem;
  cursor: pointer; color: #7f8c8d;
}
.ac-modal__body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.ac-modal__footer {
  padding: 14px 20px;
  display: flex; justify-content: flex-end; gap: 10px;
  border-top: 1px solid #eaeaea;
}

.ac-field { display: flex; flex-direction: column; gap: 6px; }
.ac-field label { font-size: 0.88rem; font-weight: 600; color: #444; }
.ac-req { color: #e74c3c; }
.ac-input {
  padding: 9px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;
  transition: border .2s;
}
.ac-input:focus { border-color: #2E3A59; }
.ac-err { color: #e74c3c; font-size: 0.82rem; margin: 0; }
.ac-preview {
  background: #eaf4fb;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 0.88rem;
  color: #1a5276;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ac-preview-sub {
  font-size: 0.8rem;
  color: #2471a3;
  font-weight: 400;
}
.ac-cycle {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.82rem;
  color: #555;
}
.ac-days-left {
  font-size: 0.78rem;
  color: #e67e22;
  font-weight: 700;
}
.ac-no-cycle {
  font-size: 0.82rem;
  color: #aaa;
}
</style>
